import type { RewardsAndCreditsLineItem } from "@apac/hotels";
import type { FiatPrice } from "@b2bportal/core-types";
import type { Product, Prices } from "@b2bportal/purchase-api";
import {
  CardPaymentSelectors,
  useCheckoutState,
  useCheckoutStateSelector,
} from "@checkout/index";
import { ParentState } from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { RewardsPrice } from "@hopper-b2b/types";
import { useCheckoutRemainingBalance } from "./useCheckoutRemainingBalance";
import { usePointsInputValidator } from "./usePointsInputValidator";
import {
  RewardsSelectedOptionType,
  WalletPaymentSelectors,
  useParentState,
} from "@/modules/checkout";

export interface UseBalanceWithPointsReturn {
  remainingBalance: FiatPrice;
  rewardsAmountToApply: RewardsPrice;
  isFulfillDisabled: boolean;
  isConversionLoading?: boolean;
  rewardsAndCreditsLineItems: RewardsAndCreditsLineItem[];
  totalCoveredByRewards: boolean;
  isConfirmationPage: boolean;
}

export const useBalanceWithPoints = (
  product?: Product,
  finalTripTotal?: Prices
): UseBalanceWithPointsReturn => {
  const [state] = useCheckoutState();
  const { t, formatRewardsCurrency, formatFiatCurrency } = useI18nContext();
  const { parentStateContext } = useParentState();

  const hasSelectedPayment = !!useCheckoutStateSelector(
    CardPaymentSelectors.getSelectedPaymentMethodId
  );

  const offerDiscount = useCheckoutStateSelector(
    WalletPaymentSelectors.getAppliedOfferDiscountAmount
  );

  const { isValid: isPointsInputValid } = usePointsInputValidator();

  const {
    remainingBalance,
    tripTotal,
    isConfirmationPage,
    rewardsAmountToApply,
    rewardsAmountToApplyCashEquivalent,
  } = useCheckoutRemainingBalance(product, finalTripTotal);

  if (tripTotal == null) {
    return {
      remainingBalance: undefined,
      rewardsAmountToApply: undefined,
      isFulfillDisabled: true,
      isConversionLoading: false,
      rewardsAndCreditsLineItems: [],
      totalCoveredByRewards: false,
      isConfirmationPage,
    };
  }

  const rewardsSelectedState =
    state.context[ParentState.awardsPayment].rewardsSelectedState;

  const totalCoveredByRewards =
    remainingBalance?.value != null &&
    remainingBalance.value <= 0 &&
    rewardsSelectedState === RewardsSelectedOptionType.APPLY_POINTS;

  const isFulfillDisabled = !(
    parentStateContext.hasCompletedPassengerInformationStep &&
    isPointsInputValid &&
    (hasSelectedPayment || totalCoveredByRewards)
  );

  const offerLineItem: RewardsAndCreditsLineItem[] =
    offerDiscount?.fiat != null && offerDiscount.fiat.value > 0
      ? [
          {
            label: t("rewards.priceBreakdown.offerApplied"),
            value: `-${formatFiatCurrency(offerDiscount.fiat)}`,
            className: "positive",
          },
        ]
      : [];

  const rewardsLineItem: RewardsAndCreditsLineItem[] =
    rewardsAmountToApply && rewardsAmountToApply.value > 0 && isPointsInputValid
      ? [
          {
            label: t("rewards.priceBreakdown.pointsApplied", {
              points: formatRewardsCurrency(rewardsAmountToApply, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }),
            }),
            value: `-${formatFiatCurrency(rewardsAmountToApplyCashEquivalent)}`,
            className: "positive",
          },
        ]
      : [];

  const rewardsAndCreditsLineItems: RewardsAndCreditsLineItem[] = [
    ...offerLineItem,
    ...rewardsLineItem,
  ];

  return {
    remainingBalance: {
      ...remainingBalance,
    },
    rewardsAmountToApply,
    isConversionLoading: false,
    isFulfillDisabled,
    rewardsAndCreditsLineItems,
    totalCoveredByRewards,
    isConfirmationPage,
  };
};
